import type {
  SYSEditor,
  SYSFeature,
  SYSForm,
  SYSTableDefIds,
  TemplateResult,
} from "../../"
import { html, ovl, SYSConfig } from "../../"
import {
  HookFormIsDirty,
  HookFormIsDirty_ReturnType,
  HookFormIsDirty_Type,
  HookFormIsValid,
  HookFormIsValid_ReturnType,
  HookFormIsValid_Type,
} from "../../global/hooks"
import { DialogClose } from "../dialog/dialogapi"

import type {
  ControlType,
  OvlSysColumn,
  SYSTableData,
  SYSTableDef,
} from "../table/table"
import { TableGet, TableRemoveRowFromData } from "../table/tableapi"
import { _SetFormLock, InitFormType } from "./actions"
import {
  FieldIsDirty,
  FieldIsValid,
  FieldIsValidSelective,
  FieldSetFocus,
  FieldSetStateOptionsType,
  FieldSetVal,
  SYSField,
  SYSFormState,
} from "./fieldapi"
import { _setFieldInactive, _setFieldReadonly } from "./helper"
import type { Schema, SYSFormField } from "./sysformelement"
import {
  _checkFormValid,
  _getFormValid,
  /*setFormDirty*/ setVisibleValidation,
} from "./validators"

export type FormAppDefOptions = {
  labelTranslationPrefix?: string
  fallbackLabelTranslationPrefix?: string
  fieldSelectIfFocused?: boolean
}

export type FormInitOptions = {
  feature?: SYSFeature
  subFeature?: string

  initialFocusFieldKey?: string
  formId?: string
  parentFormType?: SYSForm
  parentFormId?: string
  parentFieldKey?: string
  forceOverwrite?: boolean
  forceKeepFormStateOnCancel?: boolean
  doFormChangedForEveryFieldInInit?: boolean
  tableDefId?: SYSTableDefIds
  useAlignRight?: boolean
  _fromAutoInit?: boolean
  fillSpaceIfNotVisible?: boolean
}

export type EditorFormInitOptions = {
  initialFocusFieldKey?: string
}

export const FormInit = async (
  formType: SYSForm,
  fields: { [key: string]: SYSFormField },
  options?: FormInitOptions
): Promise<GetFormReturnType> => {
  let initForm: InitFormType = {
    formType,
    fields,
    feature: options?.feature,
    subFeature: options?.subFeature,
    formId: options?.formId,
    initialFocusFieldKey: options?.initialFocusFieldKey,
    parentFormType: options?.parentFormType,
    parentFormId: options?.parentFormId,
    parentFieldKey: options?.parentFieldKey,
    forceOverwrite: options?.forceKeepFormStateOnCancel
      ? false
      : options?.forceOverwrite,
    tableDefId: options?.tableDefId,
    forceKeepFormStateOnCancel: options?.forceKeepFormStateOnCancel,
    doFormChangedForEveryFieldInInit: options?.doFormChangedForEveryFieldInInit,
    _fromAutoInit: options?._fromAutoInit,
    useAlignRight: options?.useAlignRight,
    fillSpaceIfNotVisible: options?.fillSpaceIfNotVisible,
  }
  let f = await ovl.getActions().ovl.form._InitForm(initForm)
  return FormGet(f.formType, { instance: f.formId })
}

export type GetFormOptions = {
  instance?: string
}

export type GetFormReturnType<T = any> = {
  formState: SYSFormState
  fields: { [key in keyof T]: SYSField }
}

export const FormGet = <FieldType>(
  formType: SYSForm,
  options?: GetFormOptions
): GetFormReturnType<FieldType> => {
  let instance
  if (options) {
    instance = options.instance
  }
  if (!instance) {
    instance = formType
  }
  let formState = ovl.getState().ovl.forms[formType]?.[instance]
  if (!formState) {
    return {
      formState: undefined,
      fields: {} as { [key in keyof FieldType]: SYSField },
    }
  }

  return {
    formState,
    fields: <{ [key in keyof FieldType]: SYSField }>formState.fields,
  }
}

export const FormEditorInit = async (
  // formType = editorId in this case
  formType: SYSForm,
  fields: { [key: string]: SYSFormField },
  parentFormState: SYSFormState,
  parentFieldKey: string,
  options?: EditorFormInitOptions
): Promise<SYSFormState> => {
  let initForm: InitFormType = {
    formType,
    fields,
    feature: <SYSFeature>`${parentFormState.feature}`,
    subFeature: `${parentFormState.subFeature}${formType}`,
    formId: `${parentFormState.feature}_${parentFieldKey}_${parentFormState.formId}`,
    initialFocusFieldKey: options?.initialFocusFieldKey,
    parentFormType: parentFormState.formType,
    parentFormId: parentFormState.formId,
    parentFieldKey: parentFieldKey,
    fillSpaceIfNotVisible: false,
  }
  return await ovl.getActions().ovl.form._InitForm(initForm)
}

export const FormDiscard = (formState: SYSFormState) => {
  if (!formState.tableDefId) {
    throw new Error("Ovl: FormDiscard only works on Forms backed by a Table!")
  }
  let data: SYSTableData = SYSConfig.app.tableDefIds[
    formState.tableDefId
  ].getData(ovl.getState())

  let isAdd = formState.tableDataMode === "Add"

  if (isAdd) {
    ovl.getActions().ovl.internal._TableDeleteRowFromData({
      key: formState.tableRowKey,
      def: data.tableDef[formState.tableDefId],
      data,
    })
  }

  ovl.getActions().ovl.table._TableEditClose({
    key: formState.tableRowKey,
    tableDef: data.tableDef[formState.tableDefId],
    data,
  })
  setTimeout(() => {
    ovl.getActions().ovl.form._RemoveForm(formState)
  }, 200)
}

export type FormSaveOptions = {
  suppressFeedbackSnack?: boolean
  suppressBusySnack?: boolean
}

export const FormSave = async (
  formState: SYSFormState,
  options?: FormSaveOptions
) => {
  let data: SYSTableData = SYSConfig.app.tableDefIds[
    formState.tableDefId
  ].getData(ovl.getState())
  let tableDef = data.tableDef[formState.tableDefId]
  var res = await ovl.getActions().ovl.internal._TableEditSaveRow({
    key: formState.tableRowKey,
    def: tableDef,
    data,
    formState: formState,
    suppressFeedbackSnack: options?.suppressFeedbackSnack === true,
    suppressBusySnack: options?.suppressBusySnack === true,
  })

  let result = res.results.filter((f) => f.key === formState.tableRowKey)[0]
  // if (result.type === "beforesavehook_cancel") {
  //   ovl.getActions().ovl.dialog._DialogClose({
  //     dialog: "SYSTableRowEdit",
  //     instance: `${tableDef.id}_${formState.tableRowKey}`,
  //   })
  //   return
  // }

  if (result && result.type === "error") {
    return
  }

  if (FormIsValid(formState) || result.type === "beforesavehook_cancel") {
    if (formState.isInline) {
      ovl.getActions().ovl.table._TableEditClose({
        key: formState.tableRowKey,
        tableDef,
        data,
      })
    } else {
      DialogClose("SYSTableRowEdit", {
        instance: `${tableDef.id}_${formState.tableRowKey}`,
      })
    }
    // new 04.04.2024 .. always removform. was before only in the not inline logic
    FormRemove(formState.formType, formState.formId)
  }

  //dont use formState.TableRowMode...That gets changed in EditSaveRowHelper for currently unknown reason
  let ovlSysState = data.data[formState.tableRowKey]
    ?.OvlSysState as OvlSysColumn
  // check for ovlSysState because if backendDecidedToMakeUpdate then it's all already set
  // and there won't be an entry at formState.tableRowKey
  if (ovlSysState) {
    let isAdd = ovlSysState.editRow.mode !== "edit"
    if (isAdd) {
      if (result.type !== "beforesavehook_cancel") {
        if (
          tableDef.uiState.dataFilteredAndSorted.indexOf(
            formState.tableRowKey
          ) < 0
        ) {
          //push it, if not done already in inline edit
          if (tableDef.options.addedRowsPosition === "bottom") {
            tableDef.uiState.dataFilteredAndSorted.push(formState.tableRowKey)
          } else {
            tableDef.uiState.dataFilteredAndSorted.unshift(
              formState.tableRowKey
            )
          }
        }
      } else {
        TableRemoveRowFromData(tableDef.id, formState.tableRowKey)
      }
    }
    ovlSysState.editRow.mode = undefined
  }
}

export const FormReset = (formState) => {
  ovl.getActions().ovl.form._ResetForm(formState)
}

export const FormRemove = (formType: string, formId?: string) => {
  if (!formId) {
    formId = formType
  }
  ovl.getActions().ovl.form._RemoveForm({ formId, formType })
}

export const FormSetLock = (formState: SYSFormState, lock: boolean) => {
  _SetFormLock({ formState, locked: lock })
}

export const FormSetGroupState = (
  formState: SYSFormState,
  group: number,
  active: boolean,
  options?: FieldSetStateOptionsType
) => {
  let groupState = formState.groups.edit[group]
  if (
    groupState === undefined ||
    groupState.removedAndIrrelevantInCurrentSituation
  ) {
    return
  }

  let fields = formState.fields
  let validFields = Object.keys(fields)
    .filter((f) => {
      return fields[f].groups.group === group
    })
    .map((m) => fields[m])
  let first = true

  let mode = options?.mode
  if (!mode) {
    mode = "visible/notvisible"
  }

  validFields.forEach((field) => {
    _setFieldInactive(
      field,
      !active,
      options?.animate,
      mode,
      // focus on the first
      first && options?.focusWhenVisible,
      true,
      options?.forceValueWhenInactive,
      options?.forceValue,
      options?.keepValidation,
      false,
      first && options?.focusForce
    )
    first = false
    if (active) {
      if (mode === "readonly/notvisible") {
        _setFieldReadonly(field, true)
      } else if (mode === "visible/readonly") {
        _setFieldReadonly(field, false)
      }
    } else if (mode === "visible/readonly") {
      _setFieldReadonly(field, true)
    }
  })

  if (!active) {
    if (mode === "readonly/notvisible" || mode === "visible/notvisible") {
      groupState.visible = "false"
    }
  } else {
    groupState.visible = "true"
  }
  groupState.inactive = !active
}

export type FormSetGroupValOptionsType = {
  setFocus?: boolean
  clearList?: boolean
}

export const FormSetGroupVal = async (
  formState: SYSFormState,
  group: number,
  val: any,
  options?: FormSetGroupValOptionsType
) => {
  let groupState = formState.groups.edit[group]
  if (
    groupState === undefined ||
    groupState.removedAndIrrelevantInCurrentSituation
  ) {
    return
  }
  let fields = formState.fields
  let f_options = { clearList: options?.clearList }
  let setFocus = options?.setFocus
  let validFields = Object.keys(fields)
    .filter((f) => {
      return fields[f].groups.group === group
    })
    .map((m) => fields[m])
  await Promise.all(
    validFields.map(async (field) => {
      await FieldSetVal(field, val, f_options)
    })
  )
  if (setFocus) {
    await FormSetGroupFocusToFirstActive(formState, group)
  }
}

export const FormSetGroupFocusToFirstActive = (
  formState: SYSFormState,
  group: number
) => {
  let groupState = formState.groups.edit[group]
  if (
    groupState === undefined ||
    groupState.removedAndIrrelevantInCurrentSituation
  ) {
    return
  }
  let fields = formState.fields

  let validFields = Object.keys(fields)
    .filter((f) => {
      return fields[f].groups.group === group
    })
    .map((m) => fields[m])
  validFields.some((field) => {
    if (!field.inactive && !field.ui.readonly) {
      FieldSetFocus(field)
      return true
    }
  })
}

export const FormEditGroupIsValidSelective = (
  formState: SYSFormState,
  group: number,
  options?: IsFieldValidOptions
) => {
  let keys = Object.keys(formState.fields).filter(
    (f) => formState.fields[f] && formState.fields[f].groups.group === group
  )
  return !keys.some((k) => !FieldIsValidSelective(formState.fields[k], options))
}

export const FormEditGroupIsValid = (
  formState: SYSFormState,
  group: number
) => {
  let keys = Object.keys(formState.fields).filter(
    (f) => formState.fields[f] && formState.fields[f].groups.group === group
  )
  if (formState._state._inInitOrChangedCount > 0) {
    return !keys.some((k) => !FieldIsValidSelective(formState.fields[k]))
  }
  return !keys.some((k) => !FieldIsValid(formState.fields[k]))
}

export const FormEditTabSet = (formState: SYSFormState, tab: number) => {
  if (tab !== formState._state._activeEditTab) {
    formState._state._activeEditTab = tab
  }
}

export const FormEditTabGet = (formState: SYSFormState) => {
  return formState._state._activeEditTab
}

// export const FormEditTabIsValidSelective = (
//   formState: SYSFormState,
//   tab: number,
//   options?: IsFieldValidOptions
// ) => {
//   let keys = Object.keys(formState.fields).filter(
//     (f) => formState.fields[f] && formState.fields[f].groups.tab === tab
//   )
//   return !keys.some((k) => !FieldIsValidSelective(formState.fields[k], options))
// }

export const FormEditTabIsValid = (formState: SYSFormState, tab: number) => {
  return _checkFormValid(formState, tab)
  // let keys = Object.keys(formState.fields).filter(
  //   (f) => formState.fields[f] && formState.fields[f].groups.tab === tab
  // )
  // if (formState._state._inInitOrChangedCount > 0) {
  //   return !keys.some((k) => !FieldIsValidSelective(formState.fields[k]))
  // }
  // return !keys.some((k) => !FieldIsValid(formState.fields[k]))
}

export type IsFieldValidOptions = {
  ignoreValidationKeys?: string[]
  ignoreSystemValidations?: boolean
  ignoreValidateValidations?: boolean
  ignoreValueChangedValidations?: boolean
}

export const FormIsValid = (formState: SYSFormState) => {
  if (formState === undefined) {
    return false
  }
  let formIsValid = _getFormValid(formState)
  let fn = ovl.getActions().app.features[formState.feature]
  let hookFormIsValid =
    fn?.hooks?.[HookFormIsValid.replace("{subFeature}", formState.subFeature)]
  if (hookFormIsValid) {
    let validRes: HookFormIsValid_ReturnType = hookFormIsValid(<
      HookFormIsValid_Type
    >{
      formState,
      validFromFields: formIsValid,
    })
    if (validRes?.forceIsInvalidTrue === true) {
      return false
    }
  }
  return formIsValid
}

export const FormIsDirty = (formState: SYSFormState) => {
  if (formState === undefined) {
    return false
  }
  let fn = ovl.getActions().app.features[formState.feature]
  let fields = formState.fields
  let isDirty = Object.keys(fields).some((s) => {
    let res = FieldIsDirty(fields[s])
    return res
  })

  let hookFormIsDirty =
    fn?.hooks?.[HookFormIsDirty.replace("{subFeature}", formState.subFeature)]
  if (hookFormIsDirty) {
    let dirtyRes: HookFormIsDirty_ReturnType = hookFormIsDirty(<
      HookFormIsDirty_Type
    >{
      formState,
      dirtyFromFields: isDirty,
    })
    if (dirtyRes?.forceIsDirtyTrue === true) {
      return true
    }
  }
  return isDirty
}

export const FormSetUnDirty = (formState: SYSFormState) => {
  if (formState === undefined) {
    return
  }
  if (formState.locked) {
    formState.locked = false
  }
  Object.keys(formState.fields).forEach((k) => {
    // new 11.04.24 when undirty form and keeping form then set the new original values
    // we do a JSON.parse(--- dance here because it could be type object (eg. U_ITFPrtUserData)
    formState.fields[k].originalValue = JSON.parse(
      JSON.stringify(formState.fields[k].convertedValue)
    )
    if (formState.fields[k].touched) {
      formState.fields[k].touched = false
    }
  })
}

export const FormGetDirtyFields = (
  formState: SYSFormState
): {
  formDirty: boolean
  fields: { [key: string]: SYSField }
  dirtyFieldsCount: number
} => {
  let hasDirty = false
  let dirtyFieldsCount = 0
  let fieldsRes = {}
  if (formState) {
    let fields = formState.fields
    fieldsRes = Object.keys(fields)
      .filter((f) => FieldIsDirty(fields[f]))
      .reduce((val, k) => {
        hasDirty = true
        dirtyFieldsCount++
        val[k] = fields[k]
        return val
      }, {})
  }
  return { fields: fieldsRes, formDirty: hasDirty, dirtyFieldsCount }
}

export const FormGetControl = (
  field: SYSField,
  controlTypeFromTableDefId?: SYSTableDef,
  useControlType?: ControlType
): TemplateResult => {
  let controlType: ControlType
  if (field.control) {
    controlType = field.control
  }
  if (!controlType) {
    if (useControlType) {
      controlType = useControlType
    } else if (controlTypeFromTableDefId) {
      controlType = controlTypeFromTableDefId.columns[field.fieldKey].control
    } else {
      throw Error(
        "ovl TableFormGetControl needs either: tableDefId or controltype param..."
      )
    }
  }
  let uiItem
  switch (controlType) {
    case "text":
      uiItem = html`
        <ovl-textbox class="fd-form__item " .props=${() => field}>
        </ovl-textbox>
      `
      break

    case "date":
      uiItem = html`
        <ovl-datebox class="fd-form__item " .props=${() => field}>
        </ovl-datebox>
      `
      break

    case "time":
      uiItem = html`
        <ovl-timebox class="fd-form__item " .props=${() => field}>
        </ovl-timebox>
      `
      break
    case "datetime":
      uiItem = html`
          <ovl-datetimebox
            class="fd-form__item "
            .props=${() => field}
          >
          </ovl-datebox>
        `
      break

    case "textarea":
      uiItem = html`
        <ovl-textarea class="fd-form__item " .props=${() => field}>
        </ovl-textarea>
      `
      break

    case "list":
      {
        uiItem = html`
          <ovl-listcontrol class="fd-form__item " .props=${() => field}>
          </ovl-listcontrol>
        `
      }
      break
    case "option":
      {
        uiItem = html` <ovl-option .props=${() => field}> </ovl-option> `
      }
      break

    case "asset":
      {
        uiItem = html`
          <ovl-asset class="fd-form__item " .props=${() => field}> </ovl-asset>
        `
      }
      break

    case "checkbox":
      {
        uiItem = html`
          <ovl-checkbox class="fd-form__item " .props=${() => field}>
          </ovl-checkbox>
        `
      }
      break
  }
  return uiItem
}

export const FormIsLocked = (formState: SYSFormState) => {
  return formState.locked
}
export { HasVisibleSummaryErrors as FormHasVisibleSummaryErrors } from "./validators"

// export { GetFormValidationErrors as FormGetValidationErrors } from "../../library/forms/actions"
export type { SYSFormState } from "../../library/forms/actions"
